import { useState } from "react";
import type { HoursSchedule, ProfileField } from "@starsach/shared-types";
import { useSetOverride } from "../../api/hooks";
import { Dialog, ErrorState, btnPrimary, btnSecondary, inputClass } from "../../components/ui";

/**
 * Emergency close (requirements 8.9, the owner's ask of 2026-09-18): one
 * button, during an incident, by someone who is not a developer. It is
 * an override on the opening hours - an empty week plus the announcement
 * - so nothing authored changes, it ends on its expiry or by hand, and
 * the audit log records it like any other override. The confirmation
 * names the profile and the environment, because a live environment is
 * exactly where this is pressed in a hurry.
 */
export function EmergencyClose({
  environmentId,
  envName,
  profileId,
  profileName,
  hoursFields,
  onClose,
}: {
  environmentId: string;
  envName: string;
  profileId: string;
  profileName: string;
  hoursFields: Array<Extract<ProfileField, { type: "hours" }>>;
  onClose: () => void;
}) {
  const set = useSetOverride(environmentId);
  const [fieldName, setFieldName] = useState(hoursFields[0]!.name);
  const field = hoursFields.find((f) => f.name === fieldName) ?? hoursFields[0]!;
  const [announcement, setAnnouncement] = useState(field.value.closed_announcement ?? "We are closed at the moment. Please call back later.");
  const [reason, setReason] = useState("");
  const [expiresAt, setExpiresAt] = useState("");

  const value: HoursSchedule = { timezone: field.value.timezone, weekly: [], holidays: [], closed_announcement: announcement };

  return (
    <Dialog title={`Close ${profileName} in ${envName} now?`} onClose={onClose}>
      {set.isSuccess ? (
        <>
          <p role="status" className="text-sm">
            <strong>{profileName}</strong> is closed in <strong>{envName}</strong>. Callers hear <q>{announcement}</q>
            {expiresAt ? ` until ${new Date(expiresAt).toLocaleString()}` : " until someone ends the override"}.
          </p>
          <button type="button" className={`${btnPrimary} mt-4`} onClick={onClose}>Done</button>
        </>
      ) : (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            set.mutate({ profileId, field: `hours/${field.name}`, value, reason, ...(expiresAt ? { expiresAt: new Date(expiresAt).toISOString() } : {}) });
          }}
        >
          <p className="text-sm text-neutral-700 dark:text-neutral-300">
            Every caller to this profile in <strong>{envName}</strong> hears the announcement and is treated as out of hours, from now until the time below or until the override is ended. Nothing you have authored changes.
          </p>
          {hoursFields.length > 1 && (
            <label className="block text-sm">
              Which hours
              <select className={inputClass} value={fieldName} onChange={(e) => setFieldName(e.target.value)}>
                {hoursFields.map((f) => <option key={f.name} value={f.name}>{f.name}</option>)}
              </select>
            </label>
          )}
          <label className="block text-sm">
            Announcement callers hear
            <textarea className={`${inputClass} min-h-20`} value={announcement} onChange={(e) => setAnnouncement(e.target.value)} required maxLength={4000} />
          </label>
          <label className="block text-sm">
            Why
            <input className={inputClass} value={reason} onChange={(e) => setReason(e.target.value)} required placeholder="Building evacuated" />
          </label>
          <label className="block text-sm">
            Until
            <input type="datetime-local" className={inputClass} value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} aria-describedby="close-until-hint" />
            <span id="close-until-hint" className="mt-1 block text-xs text-neutral-500">Leave empty to stay closed until someone ends it. An override with no expiry is shown as ageing until then.</span>
          </label>
          <div className="flex gap-2">
            <button type="submit" className={btnPrimary} disabled={set.isPending}>
              {set.isPending ? "Closing…" : `Close ${profileName} in ${envName} now`}
            </button>
            <button type="button" className={btnSecondary} onClick={onClose}>Cancel</button>
          </div>
          {set.isError && <ErrorState error={set.error} />}
        </form>
      )}
    </Dialog>
  );
}

/** An hours override with no open spans is a closure; anything else reads as the value it plays. */
export function describeOverride(value: unknown): string | undefined {
  if (value && typeof value === "object" && "weekly" in value) {
    const v = value as HoursSchedule;
    return v.weekly.length === 0 ? `Closed (emergency close)${v.closed_announcement ? ` - callers hear "${v.closed_announcement}"` : ""}` : "Different hours apply";
  }
  return undefined;
}
